import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useBusinesses } from "@/hooks/use-businesses";
import { apiRequest } from "@/lib/queryClient";
import { Upload, Link2, Unlink, Search, Receipt, CheckCircle, AlertTriangle, Loader2 } from "lucide-react";

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const statusBadge = (s: string) => {
  const c: Record<string, string> = { matched: "bg-green-100 text-green-800", suggested: "bg-yellow-100 text-yellow-800", unmatched: "bg-gray-100 text-gray-800" };
  return <Badge className={c[s] || c.unmatched}>{s}</Badge>;
};

export default function ReceiptsPage() {
  const { activeBusiness } = useBusinesses();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const businessId = activeBusiness?.id;

  const [showUpload, setShowUpload] = useState(false);
  const [fileName, setFileName] = useState("");
  const [vendor, setVendor] = useState("");
  const [amount, setAmount] = useState("");
  const [receiptDate, setReceiptDate] = useState("");
  const [showCandidates, setShowCandidates] = useState<number | null>(null);

  const { data: receipts = [], isLoading } = useQuery({
    queryKey: ["/api/businesses", businessId, "receipts"],
    queryFn: async () => { const r = await apiRequest("GET", `/api/businesses/${businessId}/receipts`); return r.json(); },
    enabled: !!businessId,
  });

  const { data: candidates = [] } = useQuery({
    queryKey: ["/api/businesses", businessId, "receipts", showCandidates, "candidates"],
    queryFn: async () => { const r = await apiRequest("GET", `/api/businesses/${businessId}/receipts/${showCandidates}/candidates`); return r.json(); },
    enabled: !!showCandidates,
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("POST", `/api/businesses/${businessId}/receipts`, {
        file_name: fileName, file_url: `/uploads/${fileName}`,
        vendor_name: vendor || undefined, amount: amount ? parseFloat(amount) : undefined,
        receipt_date: receiptDate || undefined,
      });
      return r.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/businesses", businessId, "receipts"] });
      const matchCount = data.matches?.length || 0;
      toast({ title: "Receipt uploaded", description: matchCount > 0 ? `Found ${matchCount} potential match${matchCount > 1 ? "es" : ""}` : "No matches found yet" });
      setShowUpload(false); setFileName(""); setVendor(""); setAmount(""); setReceiptDate("");
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const matchMutation = useMutation({
    mutationFn: async ({ receiptId, transactionId }: { receiptId: number; transactionId: number }) => {
      await apiRequest("POST", `/api/businesses/${businessId}/receipts/${receiptId}/match`, { transaction_id: transactionId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/businesses", businessId, "receipts"] });
      setShowCandidates(null);
      toast({ title: "Receipt matched" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const unmatchMutation = useMutation({
    mutationFn: async (receiptId: number) => {
      await apiRequest("POST", `/api/businesses/${businessId}/receipts/${receiptId}/unmatch`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/businesses", businessId, "receipts"] });
      toast({ title: "Receipt unmatched" });
    },
  });

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Receipt className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Receipts</h1>
              <p className="text-muted-foreground text-sm">Upload and match receipts to transactions</p>
            </div>
          </div>
          <Button onClick={() => setShowUpload(true)} className="gap-2"><Upload className="h-4 w-4" /> Upload Receipt</Button>
        </div>

        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12" />)}</div>
            ) : receipts.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <Receipt className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p>No receipts yet. Upload your first receipt to get started.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {receipts.map((r: any) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm font-medium">{r.file_name || "receipt"}</TableCell>
                      <TableCell className="text-sm">{r.vendor_name || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{r.amount ? fmt(Number(r.amount)) : "—"}</TableCell>
                      <TableCell className="text-sm">{r.receipt_date ? new Date(r.receipt_date).toLocaleDateString() : "—"}</TableCell>
                      <TableCell>
                        {r.match_confidence > 0 && (
                          <Badge className={r.match_confidence >= 80 ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}>
                            {r.match_confidence}%
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{statusBadge(r.status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {r.status !== "matched" && (
                            <Button size="sm" variant="outline" onClick={() => setShowCandidates(r.id)} className="gap-1">
                              <Search className="h-3 w-3" /> Match
                            </Button>
                          )}
                          {r.status === "matched" && (
                            <Button size="sm" variant="ghost" onClick={() => unmatchMutation.mutate(r.id)} className="gap-1">
                              <Unlink className="h-3 w-3" /> Unlink
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Upload Modal */}
        <Dialog open={showUpload} onOpenChange={setShowUpload}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Receipt</DialogTitle>
              <DialogDescription>Enter receipt details for matching.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>File Name</Label><Input value={fileName} onChange={e => setFileName(e.target.value)} placeholder="receipt_amazon_25.99.pdf" /></div>
              <div><Label>Vendor (optional)</Label><Input value={vendor} onChange={e => setVendor(e.target.value)} placeholder="Amazon" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount (optional)</Label><Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="25.99" /></div>
                <div><Label>Date (optional)</Label><Input type="date" value={receiptDate} onChange={e => setReceiptDate(e.target.value)} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
              <Button onClick={() => uploadMutation.mutate()} disabled={!fileName || uploadMutation.isPending} className="gap-2">
                {uploadMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Upload & Match
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Candidates Modal */}
        <Dialog open={!!showCandidates} onOpenChange={() => setShowCandidates(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Match to Transaction</DialogTitle>
              <DialogDescription>Select the matching transaction for this receipt.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {candidates.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  <AlertTriangle className="h-6 w-6 mx-auto mb-2 opacity-40" />
                  No candidate transactions found.
                </div>
              ) : (
                candidates.map((c: any) => (
                  <div key={c.transactionId} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                    <div className="flex-1">
                      <div className="text-sm font-medium">{c.description}</div>
                      <div className="text-xs text-muted-foreground">{new Date(c.date).toLocaleDateString()} · {fmt(c.amount)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={c.score >= 80 ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}>{c.score}%</Badge>
                      <Button size="sm" onClick={() => matchMutation.mutate({ receiptId: showCandidates!, transactionId: c.transactionId })} disabled={matchMutation.isPending}>
                        <Link2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
